'use client';

import { useDeleteListItemMutation } from 'src/api/redux/lists/listsApi';
import { useLogging } from 'src/context/logging';
import { useProspectSelection } from 'src/context/prospects';
import { useNotification } from 'src/context/notification';
import { Dialog, PulseButton } from 'src/components/PulseUI';
import useLists from 'src/hooks/lists/useLists';

interface RemoveProvidersDialogProps {
  open: boolean;
  onClose: (event?: any, reason?: string) => void;
}

/**
 * Dialog to confirm the removal of a prospect from a list
 */
const RemoveProvidersDialog: React.FC<RemoveProvidersDialogProps> = ({
  open,
  onClose,
}) => {
  const { setNotification } = useNotification();
  const [deleteListItem, { isLoading: isDeletingProvider }] =
    useDeleteListItemMutation();
  const { selectedList, triggerListMembersRefetch } = useLists();

  const log = useLogging();

  const {
    state: [selectedProviders, setSelectedProviders],
  } = useProspectSelection();

  const handleDeleteProspect = async (e) => {
    try {
      await Promise.all(
        selectedProviders.map((prospect) => {
          return deleteListItem({
            itemId: prospect.list_item_id,
            listId: selectedList?.id,
          }).unwrap();
        })
      );
      setNotification({
        title: 'Provider removed',
        message:
          selectedProviders.length > 1
            ? 'The providers have been removed from the list.'
            : 'The provider has been removed from the list.',
        type: 'success',
      });
      setSelectedProviders([]);
      triggerListMembersRefetch();
      onClose(e, 'backdropClick');
    } catch (err) {
      setNotification({
        title: 'Error',
        message:
          err?.error_message ||
          'An error occurred while removing the provider(s) from the list.',
        type: 'error',
      });
      log.exception(`Error deleting providers from list: ${err}`, {
        tags: {
          source: 'RemoveProvidersDialog',
          context: selectedProviders,
        },
      });
    }
  };

  return (
    <Dialog
      isOpen={open}
      onClose={() => onClose({}, 'backdropClick')}
      size="sm"
      className="!h-auto"
      data-testid="remove-providers-dialog"
      aria-labelledby="remove-providers-dialog-title"
    >
      <Dialog.Header>
        <h2
          id="remove-providers-dialog-title"
          className="text-lg font-semibold"
          data-testid="remove-providers-dialog-title"
        >
          Delete {selectedProviders.length === 1 ? 'Provider' : 'Providers'} from
          List
        </h2>
      </Dialog.Header>
      <Dialog.Content className="p-6" data-testid="remove-providers-dialog-content">
        <p className="text-neutral-700">
          Are you sure you want to remove&nbsp;
          {selectedProviders.length === 1 ? (
            <strong>{selectedProviders[0]?.name}</strong>
          ) : (
            <strong>{selectedProviders.length} providers</strong>
          )}
          ?
        </p>
      </Dialog.Content>
      <Dialog.Actions data-testid="remove-providers-dialog-actions">
        <PulseButton
          variant="outline"
          color="neutral"
          disabled={isDeletingProvider}
          onClick={(e) => {
            onClose(e, 'backdropClick');
          }}
          aria-label="Cancel"
          data-testid="remove-providers-cancel-button"
        >
          Cancel
        </PulseButton>
        <PulseButton
          variant="solid"
          color="primary"
          loading={isDeletingProvider}
          onClick={(e) => handleDeleteProspect(e)}
          aria-label="Confirm removal"
          data-testid="remove-providers-confirm-button"
        >
          Confirm
        </PulseButton>
      </Dialog.Actions>
    </Dialog>
  );
};

export default RemoveProvidersDialog;
